import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';

const InterviewPreferencesSection = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [preferences, setPreferences] = useState({
    defaultLanguage: "python",
    difficulty: "intermediate",
    sessionDuration: "30",
    voicePersonality: "professional"
  });

  const programmingLanguages = [
    { value: "python", label: "Python" },
    { value: "javascript", label: "JavaScript" },
    { value: "java", label: "Java" },
    { value: "cpp", label: "C++" },
    { value: "csharp", label: "C#" },
    { value: "sql", label: "SQL" },
    { value: "go", label: "Go" },
    { value: "rust", label: "Rust" }
  ];

  const difficultyLevels = [
    { value: "beginner", label: "Beginner", description: "Basic concepts and simple problems" },
    { value: "intermediate", label: "Intermediate", description: "Moderate complexity with real-world scenarios" },
    { value: "advanced", label: "Advanced", description: "Complex algorithms and system design" },
    { value: "expert", label: "Expert", description: "Challenging problems for senior roles" }
  ];

  const sessionDurations = [
    { value: "15", label: "15 minutes", description: "Quick practice session" },
    { value: "30", label: "30 minutes", description: "Standard interview length" },
    { value: "45", label: "45 minutes", description: "Extended practice" },
    { value: "60", label: "60 minutes", description: "Full interview simulation" }
  ];

  const voicePersonalities = [
    { value: "professional", label: "Professional", description: "Formal and business-like tone" },
    { value: "friendly", label: "Friendly", description: "Warm and encouraging approach" },
    { value: "neutral", label: "Neutral", description: "Balanced and objective style" },
    { value: "motivational", label: "Motivational", description: "Energetic and inspiring tone" }
  ];

  const handlePreferenceChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Settings" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground">
              Interview Preferences
            </h2>
            <p className="text-sm text-muted-foreground">
              Customize your interview experience
            </p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Default Programming Language"
            description="Your preferred language for coding questions"
            options={programmingLanguages}
            value={preferences?.defaultLanguage}
            onChange={(value) => handlePreferenceChange('defaultLanguage', value)}
            disabled={!isEditing}
            searchable
          />

          <Select
            label="Interview Difficulty"
            description="Adjust question complexity level"
            options={difficultyLevels}
            value={preferences?.difficulty}
            onChange={(value) => handlePreferenceChange('difficulty', value)}
            disabled={!isEditing}
          />

          <Select
            label="Session Duration"
            description="Default length for interview sessions"
            options={sessionDurations}
            value={preferences?.sessionDuration}
            onChange={(value) => handlePreferenceChange('sessionDuration', value)}
            disabled={!isEditing}
          />

          <Select
            label="Voice Assistant Personality"
            description="Choose interviewer communication style"
            options={voicePersonalities}
            value={preferences?.voicePersonality}
            onChange={(value) => handlePreferenceChange('voicePersonality', value)}
            disabled={!isEditing}
          />
        </div>

        {isEditing && (
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Check"
              iconPosition="left"
            >
              Save Preferences
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewPreferencesSection;